/**
 * useObstacleRendering — Bridges obstacle data to 3D rendering and collision
 *
 * Fetches obstacles for a location, converts them to ObstacleRenderData grouped
 * by building, and pushes updates to MapboxCustomLayerService and CollisionWorkerService.
 */

import { useEffect, useMemo, useRef } from 'react'
import type { MapboxCustomLayerService } from '@/services/3d/MapboxCustomLayerService'
import type { ObstacleRenderData } from '@/services/3d/rendering/ObstacleRenderer'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'
import { useObstaclesByLocation } from '@/hooks/useObstacles'
import type { Obstacle } from '@/types/obstacle'
import { FEET_TO_METERS } from '@/constants/units'
import type { SerializedObstacleGeometry } from '@/services/collision/worker/collision.worker.types'
import { getCollisionWorkerService } from '@/services/collision/worker/CollisionWorkerService'

/** Building position + rotation info needed for obstacle world-space transform */
interface BuildingTransform {
  position: [number, number, number] // [x, y, z] in feet
  rotation: number // degrees
}

interface UseObstacleRenderingParams {
  locationId: string | undefined
  buildings: Building3DConfigExtended[]
  layerService: MapboxCustomLayerService | null
  /** Whether to send obstacle geometry to the collision worker */
  collisionEnabled?: boolean
  /** Increment after buildings are ready in the scene to re-trigger push */
  syncTrigger?: number
}

/**
 * Groups obstacles by building ID (via unit → building lookup),
 * converts to ObstacleRenderData, and pushes to 3D renderers.
 */
export function useObstacleRendering({
  locationId,
  buildings,
  layerService,
  collisionEnabled = false,
  syncTrigger,
}: UseObstacleRenderingParams) {
  const { data: obstacles = [] } = useObstaclesByLocation(locationId)

  // Build unit→building lookup, unit→offset lookup, and building→transform lookup
  const { unitToBuildingMap, unitOffsetMap, buildingTransformMap } = useMemo(() => {
    const u2b = new Map<string, string>()
    const u2o = new Map<string, [number, number]>()
    const b2t = new Map<string, BuildingTransform>()

    for (const building of buildings) {
      if (!building.generatedUnits) continue
      // Apply the same centering shift that orchestrators apply to unitsGroup
      const centerX = building.width / 2
      const tpl = building.templateType as string | undefined
      const centerZ = (tpl === 'double_row' || tpl === 'nested_t_hangar') ? building.depth / 2 : 0

      b2t.set(building.id, {
        position: building.position,
        rotation: building.rotation ?? 0,
      })

      for (const unit of building.generatedUnits) {
        if (!unit.id) continue
        u2b.set(unit.id, building.id)
        u2o.set(unit.id, [unit.offset_x - centerX, unit.offset_y - centerZ])
      }
    }
    return { unitToBuildingMap: u2b, unitOffsetMap: u2o, buildingTransformMap: b2t }
  }, [buildings])

  // Group obstacles by building and compute render data
  const renderDataByBuilding = useMemo(() => {
    if (obstacles.length === 0 || unitToBuildingMap.size === 0) return new Map<string, ObstacleRenderData[]>()

    const map = new Map<string, Map<string, ObstacleRenderData>>()

    for (const obstacle of obstacles) {
      const buildingId = unitToBuildingMap.get(obstacle.unitId)
      if (!buildingId) continue

      const offset = unitOffsetMap.get(obstacle.unitId) ?? [0, 0]

      if (!map.has(buildingId)) {
        map.set(buildingId, new Map())
      }
      const byUnit = map.get(buildingId)!

      if (!byUnit.has(obstacle.unitId)) {
        byUnit.set(obstacle.unitId, { obstacles: [], unitOffset: offset })
      }
      byUnit.get(obstacle.unitId)!.obstacles.push(obstacle)
    }

    // Flatten to arrays
    const result = new Map<string, ObstacleRenderData[]>()
    for (const [buildingId, byUnit] of map) {
      result.set(buildingId, Array.from(byUnit.values()))
    }
    return result
  }, [obstacles, unitToBuildingMap, unitOffsetMap])

  // Push obstacle render data to 3D layer
  const prevKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!layerService) return

    const currentKeys = new Set(renderDataByBuilding.keys())

    // Update or create obstacle renderers for buildings that have obstacles
    for (const [buildingId, renderData] of renderDataByBuilding) {
      layerService.updateBuildingObstacles(buildingId, renderData)
    }

    // Remove obstacle renderers for buildings that no longer have obstacles
    for (const prevKey of prevKeysRef.current) {
      if (!currentKeys.has(prevKey)) {
        layerService.removeBuildingObstacles(prevKey)
      }
    }

    prevKeysRef.current = currentKeys
  }, [renderDataByBuilding, layerService, syncTrigger])

  // Send obstacle geometry to collision worker
  useEffect(() => {
    if (!collisionEnabled || obstacles.length === 0 || unitToBuildingMap.size === 0) return

    const serialized = serializeObstaclesForWorker(obstacles, unitOffsetMap, unitToBuildingMap, buildingTransformMap)
    if (serialized.meshes.length === 0) return

    try {
      const workerService = getCollisionWorkerService()
      workerService.updateObstacleGeometry(serialized)
    } catch (e) {
      // Worker may not be initialized — that's ok, collision falls back to sync
    }
  }, [obstacles, collisionEnabled, unitToBuildingMap, unitOffsetMap, buildingTransformMap])

  return {
    obstacles,
    renderDataByBuilding,
  }
}

/**
 * Serialize obstacles into flat geometry for the collision worker.
 * Each obstacle becomes a single box or cylinder mesh with pre-baked transforms.
 */
function serializeObstaclesForWorker(
  obstacles: Obstacle[],
  unitOffsetMap: Map<string, [number, number]>,
  unitToBuildingMap: Map<string, string>,
  buildingTransformMap: Map<string, BuildingTransform>,
): SerializedObstacleGeometry {
  const meshes: SerializedObstacleGeometry['meshes'] = []
  const obstacleInfo: SerializedObstacleGeometry['obstacleInfo'] = []

  for (const obs of obstacles) {
    if (!obs.participatesInCollision || !obs.isActive) continue

    const buildingId = unitToBuildingMap.get(obs.unitId)
    if (!buildingId) continue

    const offset = unitOffsetMap.get(obs.unitId) ?? [0, 0]
    const buildingTransform = buildingTransformMap.get(buildingId)

    // Step 1-2: Obstacle position in building-local space (centered, feet)
    const centeredX = offset[0] + obs.positionX
    const centeredZ = -(offset[1] + obs.positionY)

    // Step 3: Apply building rotation (Three.js Y-axis rotation)
    const rotationRad = ((buildingTransform?.rotation ?? 0) * Math.PI) / 180
    const cos = Math.cos(rotationRad)
    const sin = Math.sin(rotationRad)
    const rotatedX = centeredX * cos + centeredZ * sin
    const rotatedZ = -centeredX * sin + centeredZ * cos

    // Step 4: Add building world position (feet) then convert to meters
    const bPos = buildingTransform?.position ?? [0, 0, 0]
    const posX = (bPos[0] + rotatedX) * FEET_TO_METERS
    const posY = obs.elevation * FEET_TO_METERS
    const posZ = (bPos[2] + rotatedZ) * FEET_TO_METERS

    let positions: Float32Array
    let indices: Uint32Array

    if (obs.shape === 'circle' && obs.radius != null) {
      // Approximate circle as 8-sided cylinder (16 vertices)
      const r = obs.radius * FEET_TO_METERS
      const h = obs.height * FEET_TO_METERS
      const sides = 8
      const verts = new Float32Array(sides * 2 * 3)
      const idx: number[] = []

      for (let i = 0; i < sides; i++) {
        const angle = (i / sides) * Math.PI * 2
        const cx = Math.cos(angle) * r + posX
        const cz = Math.sin(angle) * r + posZ

        // Bottom vertex
        verts[i * 6] = cx
        verts[i * 6 + 1] = posY
        verts[i * 6 + 2] = cz
        // Top vertex
        verts[i * 6 + 3] = cx
        verts[i * 6 + 4] = posY + h
        verts[i * 6 + 5] = cz

        // Side triangles
        const next = (i + 1) % sides
        const b0 = i * 2, t0 = i * 2 + 1
        const b1 = next * 2, t1 = next * 2 + 1
        idx.push(b0, b1, t0, t0, b1, t1)
      }

      positions = verts
      indices = new Uint32Array(idx)
    } else if (obs.width != null && obs.depth != null) {
      // Rectangle box: 8 vertices, 12 triangles (6 faces)
      const hw = (obs.width * FEET_TO_METERS) / 2
      const hd = (obs.depth * FEET_TO_METERS) / 2
      const h = obs.height * FEET_TO_METERS

      // Apply obstacle rotation + building rotation (compose both)
      const totalRotDeg = obs.rotation + (buildingTransform?.rotation ?? 0)
      const rad = -(totalRotDeg * Math.PI) / 180
      const cos = Math.cos(rad)
      const sin = Math.sin(rad)

      // 4 corners at bottom (y=posY), 4 at top (y=posY+h)
      const corners = [
        [-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd],
      ]
      positions = new Float32Array(8 * 3)
      for (let i = 0; i < 4; i++) {
        const [lx, lz] = corners[i]
        const rx = lx * cos - lz * sin + posX
        const rz = lx * sin + lz * cos + posZ
        // Bottom
        positions[i * 3] = rx
        positions[i * 3 + 1] = posY
        positions[i * 3 + 2] = rz
        // Top
        positions[(i + 4) * 3] = rx
        positions[(i + 4) * 3 + 1] = posY + h
        positions[(i + 4) * 3 + 2] = rz
      }

      indices = new Uint32Array([
        // Bottom
        0, 1, 2, 0, 2, 3,
        // Top
        4, 6, 5, 4, 7, 6,
        // Front
        0, 1, 5, 0, 5, 4,
        // Back
        2, 3, 7, 2, 7, 6,
        // Left
        0, 3, 7, 0, 7, 4,
        // Right
        1, 2, 6, 1, 6, 5,
      ])
    } else {
      continue // Invalid shape
    }

    // Identity world matrix (geometry is pre-transformed)
    const worldMatrix = new Float32Array([
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1,
    ])

    meshes.push({ positions, indices, worldMatrix })
    obstacleInfo.push({
      obstacleId: obs.id,
      obstacleName: obs.name,
      unitId: obs.unitId,
      areaId: buildingId,
    })
  }

  return { meshes, obstacleInfo }
}
